import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { PendingTrainerInvite as PendingTrainerInviteType } from '@/core/AuthContext';
import { Button } from './ui/Button';
import { formatPhoneForDisplay } from '@/shared/utils/phone';

interface PendingTrainerInviteProps {
  invites: PendingTrainerInviteType[];
  isLoading?: boolean;
  onAccept: (inviteId: string) => Promise<void>;
  onDecline: (inviteId: string) => Promise<void>;
}

export function PendingTrainerInvite({
  invites,
  isLoading = false,
  onAccept,
  onDecline,
}: PendingTrainerInviteProps) {
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  const activeInvite = useMemo(() => invites[0] || null, [invites]);

  if (!activeInvite) {
    return null;
  }

  const isBusy = isLoading || busyInviteId === activeInvite.id;

  const handleAction = async (action: 'accept' | 'decline') => {
    setBusyInviteId(activeInvite.id);
    try {
      if (action === 'accept') {
        await onAccept(activeInvite.id);
      } else {
        await onDecline(activeInvite.id);
      }
    } finally {
      setBusyInviteId(null);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      >
        <motion.div
          className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
          className="relative w-full max-w-lg overflow-hidden rounded-[28px] border border-white/15 bg-white/12 p-6 text-white shadow-2xl backdrop-blur-2xl"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.22),_transparent_40%),radial-gradient(circle_at_bottom_right,_rgba(56,189,248,0.18),_transparent_38%)]" />

          <div className="relative space-y-5">
            <div className="inline-flex items-center rounded-full border border-emerald-300/25 bg-emerald-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-100">
              Trainer Invite
            </div>

            <div className="space-y-2">
              <h2 className="text-3xl font-semibold tracking-tight text-white">
                {activeInvite.trainerName} wants to train you
              </h2>
              <p className="text-sm leading-6 text-slate-200/90">
                Accept to link your account and unlock workouts, scheduling, and session tracking for{' '}
                {formatPhoneForDisplay(activeInvite.phone)}.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-slate-950/25 p-4">
              <p className="text-xs uppercase tracking-[0.22em] text-slate-300/70">Invite For</p>
              <p className="mt-2 text-lg font-medium text-white">{activeInvite.displayName || 'Client'}</p>
              <p className="mt-1 text-sm text-slate-300">{formatPhoneForDisplay(activeInvite.phone)}</p>
              {invites.length > 1 && (
                <p className="mt-3 text-xs text-amber-200/90">
                  You have {invites.length} pending trainer invites. Accepting one clears the others.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button className="flex-1" size="lg" onClick={() => handleAction('accept')} disabled={isBusy}>
                {isBusy ? 'Linking...' : 'Accept Invite'}
              </Button>
              <Button
                variant="secondary"
                className="flex-1 border-white/20 bg-white/8 text-white hover:bg-white/14"
                size="lg"
                onClick={() => handleAction('decline')}
                disabled={isBusy}
              >
                Decline
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
